import { onMounted, ref } from "vue";

export const GOOGLE_MAPS_LOADER = Symbol("google-maps-loader");

const maps = ref();

// Cached in-flight load so the script is requested at most once, even when
// `loadGoogleMapsIfNotLoaded` is called repeatedly before the first load
// resolves. Module-scoped alongside `maps` because there is a single Google
// Maps instance per page.
let loadPromise = null;

function loadGoogleMaps(key, version) {
  const url = new URL("https://maps.googleapis.com/maps/api/js");
  url.searchParams.append("key", key);
  url.searchParams.append("libraries", "places,geometry");
  if (version) {
    url.searchParams.append("v", version);
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = url.href;
    script.async = true;
    script.defer = true;

    const cleanup = () => {
      script.onload = null;
      script.onerror = null;
    };

    script.onload = () => {
      cleanup();

      // The script can load yet leave Maps unavailable (e.g. an auth failure).
      // Reject rather than throwing inside the handler, otherwise the promise
      // would never settle and every awaiting caller would hang forever.
      if (!window.google?.maps) {
        reject(new Error("Google Maps loaded but is unavailable"));
        return;
      }

      initGoogleMaps();
      resolve(maps.value);
    };
    script.onerror = () => {
      cleanup();
      reject(new Error("Failed to load Google Maps"));
    };

    document.head.appendChild(script);
  });
}

function initGoogleMaps() {
  maps.value = window.google.maps;
}

export function useGoogleMaps(props) {
  const shouldLoadOnMount = props.loadOnMount !== false;

  if (shouldLoadOnMount) {
    onMounted(() => {
      loadGoogleMapsIfNotLoaded().catch(() => {
        // Swallow load failures on mount so they don't surface as unhandled
        // rejections; an interaction-driven load will retry (the cached promise
        // is reset on failure).
      });
    });
  }

  /**
   * Loads the Google Maps script at most once and resolves when it is ready.
   *
   * Without the cached promise, every keystroke that arrives before the script
   * finishes loading would append another `maps/api/js` tag — Google then aborts
   * with "You have included the Google Maps JavaScript API multiple times" and
   * the Places library breaks on first load (works only after it is cached).
   *
   * @returns {Promise<typeof window.google.maps>}
   */
  function loadGoogleMapsIfNotLoaded() {
    if (window.google?.maps) {
      initGoogleMaps();
      return Promise.resolve(window.google.maps);
    }

    if (maps.value) {
      return Promise.resolve(maps.value);
    }

    loadPromise ??= loadGoogleMaps(props.apiKey, props.version).catch((error) => {
      // Allow a later attempt to retry after a failed load.
      loadPromise = null;
      throw error;
    });

    return loadPromise;
  }

  return {
    maps,
    loadGoogleMaps: loadGoogleMapsIfNotLoaded,
  };
}
